import Table from '@/shared/Table'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import FormAutocomplete from '@/shared/FormCreator/FormItem/FormAutocomplete'
import debounce from '@mui/material/utils/debounce'

import get from 'lodash/get'

const mapStateToProps = ({ loading }) => ({
  isLoading: loading['userGroup/getUploadSucDetail'] || loading['userGroup/getUploadErrDetail'],
})
const columnTranslationString = {
  email: 'email',
  phone: 'phone',
  cname: 'name',
  companyName: 'company_name',
  firstName: 'first_name',
  lastName: 'last_name',
  department: 'department',
  deptGroup: 'business_group',
  office: 'office',
  position: 'position',
  manager: 'manager',
  // joinOn: 'created_date',
}

const UploadPreview = ({
  dispatch,
  uuid,
  columnData,
  setUserGroup,
  userGroup,
  userGroupName,
  fileCheckResponse,
  onError,
  isLoading,
}) => {
  const { t } = useTranslation()
  const { titleList = [] } = columnData || {}

  const [result, setResult] = useState()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [groupSearch, setGroupSearch] = useState(userGroupName)
  const [searchResults, setSearchResults] = useState([])
  const hasSetDefault = useRef(false)
  const { data = [], paging = {} } = result || {}

  const error = get(fileCheckResponse, 'ERROR')

  const setGroupSearchDebounce = debounce(value => {
    setGroupSearch(value)
  }, 400)

  const pagination = {
    ...paging,
    page,
    pageSize,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
  }

  const cols = titleList.map(title => ({
    field: title === 'department' ? 'dept' : title,
    headerName: t(`general.${columnTranslationString[title]}`),
  }))

  const errorCols = [
    { field: 'rowcnt', headerName: t('general.row_count') },
    {
      field: 'error',
      flex: 1,
      headerName: t('general.error'),
      renderCell: ({ row }) => {
        const { tempUUID, type, ...rest } = row
        const errMsg = Object.values(rest)
          .filter(v => typeof v === 'string')
          .join(',')

        return <p>{errMsg}</p>
      },
    },
  ]

  useEffect(() => {
    if (uuid && fileCheckResponse) {
      dispatch({
        type: `userGroup/${error ? 'getUploadErrDetail' : 'getUploadSucDetail'}`,
        payload: { uuid, pageIndex: page, pageSize },
        successCallback: res => {
          setResult(res)
        },
      })

      if (error) {
        onError?.()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, page, pageSize, fileCheckResponse, uuid, error])

  useEffect(() => {
    setUserGroup()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileCheckResponse])

  useEffect(() => {
    dispatch({
      type: 'userGroup/getUserGroupsByName',
      payload: { searchUserGroupName: groupSearch ?? '' }, // 預設空字串
      successCallback: options => {
        const transformedOptions = Object.keys(options).map(key => ({
          label: options[key],
          id: key,
        }))
        if (transformedOptions.length > 0) {
          setSearchResults(transformedOptions)
        }
      },
    })
  }, [dispatch, groupSearch])

  useEffect(() => {
    if (!hasSetDefault.current && searchResults.length > 0) {
      const { label: defaultGroup } =
        searchResults.find(({ label }) => label === userGroupName) || {}

      if (defaultGroup) {
        setUserGroup(defaultGroup)
        hasSetDefault.current = true
      }
    }
  }, [searchResults, userGroupName, setUserGroup])

  // NOTE: Table in design is not using pagination but display more at top right of table
  // BACKEND NOTE?: phone column value should start with 09 and has 10 digits by ignoring -
  return (
    <Box sx={{ marginTop: '20px' }}>
      {error && (
        <Typography color="error" textAlign="center" my={2}>
          {t('errors.upload_list')}
        </Typography>
      )}
      {!error && (
        <FormAutocomplete
          formProps={{
            field: {
              value: userGroup || '',
              onChange: value => {
                setUserGroup(value?.label)
              },
            },
          }}
          inputConfig={{
            placeholder: t('general.select_join_group'),
            onInputChange: (_, value, reason) => {
              if (['input', 'clear'].includes(reason)) {
                setUserGroup(value)
                setGroupSearchDebounce(value)
              }
            },
            sx: { mb: 2, width: 280 },
            afterAutoComplete: () =>
              data.length > 0 && (
                <Grid container sx={{ ml: 1, mb: 2 }} alignItems="center">
                  <Typography
                    sx={{
                      color: 'rgba(0, 0, 0, 0.5)',
                      fontSize: '12px',
                      lineHeight: '16px',
                    }}
                  >
                    {t('general.confirm_upload_csv_rows_part_one')}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: '#FF6700',
                      fontSize: '12px',
                      lineHeight: '16px',
                    }}
                  >
                    &nbsp;{data.length}&nbsp;
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(0, 0, 0, 0.5)',
                      fontSize: '12px',
                      lineHeight: '16px',
                    }}
                  >
                    {t('general.confirm_upload_csv_rows_part_two')}
                  </Typography>
                </Grid>
              ),
          }}
          options={searchResults}
          disableClearable={true}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      )}

      <Table
        minHeight={300}
        columns={error ? errorCols : cols}
        rows={data}
        pagination={pagination}
        dataGridConfig={{
          getRowId: row => (error ? row.errorId : row.rowcnt),
          loading: isLoading,
        }}
      />
    </Box>
  )
}

export default connect(mapStateToProps)(UploadPreview)
